/**
 * SEO 工具函数 - 用于动态设置 meta 标签和页面标题
 * 支持 Open Graph、Twitter Card、Schema.org 结构化数据等
 */

import React from 'react';

export interface SEOConfig {
  title: string;
  description: string;
  url?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  keywords?: string;
  locale?: string;
}

export interface OpenGraphConfig extends SEOConfig {
  ogType?: string;
  ogImage?: string;
}

/**
 * 更新页面标题和基础 meta 标签
 */
export const updateSEOMeta = (config: SEOConfig): void => {
  if (typeof document === 'undefined') return;

  const {
    title,
    description,
    url,
    image,
    keywords,
    locale = 'zh_CN'
  } = config;

  // 更新页面标题
  document.title = title;

  // 获取或创建 meta 标签
  const setMeta = (name: string, content: string, isProperty = false): void => {
    let meta = document.querySelector(`meta[${isProperty ? 'property' : 'name'}="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(isProperty ? 'property' : 'name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // 基础 meta 标签
  setMeta('description', description);
  if (keywords) {
    setMeta('keywords', keywords);
  }
  setMeta('locale', locale);

  // Open Graph (Facebook、微信等)
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:type', config.type === 'article' ? 'article' : 'website', true);
  if (url) {
    setMeta('og:url', url, true);
  }
  if (image) {
    setMeta('og:image', image, true);
    setMeta('og:image:type', 'image/jpeg', true);
  }

  // Twitter Card
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  if (image) {
    setMeta('twitter:image', image);
  }
};

/**
 * 更新 canonical URL (防止重复索引)
 */
export const setCanonicalURL = (url: string): void => {
  if (typeof document === 'undefined') return;

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

/**
 * 添加 Schema.org 结构化数据 (用于 Google 搜索)
 */
export const addStructuredData = (data: Record<string, any>): void => {
  if (typeof document === 'undefined') return;

  // 检查是否已存在，避免重复
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * 生成卡片的 Schema.org 数据
 */
export const generateCardSchema = (cardData: {
  id: string;
  title: string;
  description?: string;
  image?: string;
  cardType: string;
  location?: string;
  rating?: number;
  priceRange?: string;
  url?: string;
}) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const url = cardData.url || `${baseUrl}/card/${cardData.id}`;

  let schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Thing',
    name: cardData.title,
    description: cardData.description,
    url: url,
    identifier: cardData.id,
  };

  if (cardData.image) {
    schema.image = cardData.image;
  }

  // 根据卡片类型定制 schema
  switch (cardData.cardType) {
    case 'restaurant':
      schema['@type'] = 'Restaurant';
      if (cardData.location) {
        schema.address = {
          '@type': 'PostalAddress',
          streetAddress: cardData.location,
        };
      }
      if (cardData.priceRange) {
        schema.priceRange = cardData.priceRange;
      }
      if (cardData.rating) {
        schema.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: cardData.rating,
          bestRating: '5',
          worstRating: '1',
        };
      }
      break;

    case 'lodging':
      schema['@type'] = 'LodgingBusiness';
      if (cardData.location) {
        schema.address = {
          '@type': 'PostalAddress',
          streetAddress: cardData.location,
        };
      }
      if (cardData.priceRange) {
        schema.priceRange = cardData.priceRange;
      }
      break;

    case 'poi':
      schema['@type'] = 'TouristAttraction';
      if (cardData.location) {
        schema.address = {
          '@type': 'PostalAddress',
          streetAddress: cardData.location,
        };
      }
      break;

    case 'shopping':
      schema['@type'] = 'ShoppingMall';
      if (cardData.location) {
        schema.address = {
          '@type': 'PostalAddress',
          streetAddress: cardData.location,
        };
      }
      break;

    case 'activity':
      schema['@type'] = 'Event';
      if (cardData.location) {
        schema.location = {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: cardData.location,
          },
        };
      }
      break;

    default:
      // 使用通用的 Thing 类型
      break;
  }

  return schema;
};

/**
 * React Hook - 简化在组件中使用 SEO 配置
 */
export const useSEO = (config: SEOConfig): void => {
  React.useEffect(() => {
    updateSEOMeta(config);

    // 生成 canonical URL
    if (config.url) {
      setCanonicalURL(config.url);
    }

    // 清理函数 (可选)
    return () => {
      // 如果需要，可以恢复默认的 SEO 配置
    };
  }, [config]);
};

/**
 * React Hook - 简化卡片详情页的 SEO 配置
 */
export const useCardSEO = (cardData: {
  id: string;
  title: string;
  description?: string;
  image?: string;
  cardType: string;
  location?: string;
  rating?: number;
  priceRange?: string;
}): void => {
  React.useEffect(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${baseUrl}/card/${cardData.id}`;

    // 更新基础 SEO
    updateSEOMeta({
      title: `${cardData.title} - Cubemeet`,
      description:
        cardData.description ||
        `查看 ${cardData.title} 的详情信息在 Cubemeet 上。`,
      url,
      image: cardData.image,
      keywords: `${cardData.title}, ${cardData.cardType}, 旅行, cubemeet`,
    });

    // 设置 canonical URL
    setCanonicalURL(url);

    // 添加结构化数据
    const schema = generateCardSchema({
      ...cardData,
      url,
    });
    addStructuredData(schema);
  }, [cardData]);
};

export default {
  updateSEOMeta,
  setCanonicalURL,
  addStructuredData,
  generateCardSchema,
  useSEO,
  useCardSEO,
};
